import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { PlatformHttpService } from '../../../services/platform-http.service';
import { 
  VarietyResponse, 
  VarietyListResponse, 
  CreateVarietyRequest, 
  UpdateVarietyRequest,
  VarietyFilters,
  VarietyGtin
} from '../models';
import { AuditService } from '../../../shared/models/audit-service.interface';
import { PlatformAuditResponse } from '../../../shared/models/platform-audit.model';
import { Page } from '../../../shared/models/page.model';

@Injectable({ providedIn: 'root' })
export class VarietyService implements AuditService {
  private platformHttp = inject(PlatformHttpService);
  private readonly baseUrl = '/variety';

  getVarieties(filters?: VarietyFilters): Observable<VarietyListResponse> {
    let params = new HttpParams();
    
    if (filters?.search) {
      params = params.set('q', filters.search);
    }
    if (filters?.page !== undefined) {
      params = params.set('page', filters.page.toString());
    }
    if (filters?.size) {
      params = params.set('size', filters.size.toString());
    }
    if (filters?.sort) {
      params = params.set('sort', filters.sort);
    }
    
    return this.platformHttp.get(`${this.baseUrl}/basic/paginated`, params) as Observable<VarietyListResponse>;
  }

  getVarietyById(id: number): Observable<VarietyResponse> {
    return this.platformHttp.get(`${this.baseUrl}/${id}`) as Observable<VarietyResponse>;
  }

  createVariety(variety: CreateVarietyRequest): Observable<VarietyResponse> {
    return this.platformHttp.post(this.baseUrl, variety) as Observable<VarietyResponse>;
  }

  updateVariety(variety: UpdateVarietyRequest): Observable<VarietyResponse> {
    return this.platformHttp.put(`${this.baseUrl}/${variety.id}`, variety) as Observable<VarietyResponse>;
  }

  /** GTINs of the variety's goods, per class (AI 02 on the GS1 pallet label). */
  getGtins(varietyId: number): Observable<VarietyGtin[]> {
    return this.platformHttp.get(`${this.baseUrl}/${varietyId}/gtin`) as Observable<VarietyGtin[]>;
  }

  /** Replaces all GTINs of the variety with the given list. */
  saveGtins(varietyId: number, gtins: VarietyGtin[]): Observable<VarietyGtin[]> {
    return this.platformHttp.put(`${this.baseUrl}/${varietyId}/gtin`, gtins) as Observable<VarietyGtin[]>;
  }

  deleteVariety(id: number): Observable<void> {
    return this.platformHttp.delete(`${this.baseUrl}/${id}`) as Observable<void>;
  }

  // Audit History - implements AuditService interface
  getAuditHistoryPaginated(entityId: number, page: number = 0, size: number = 20): Observable<Page<PlatformAuditResponse>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.platformHttp.get(`${this.baseUrl}/${entityId}/audit`, params) as Observable<Page<PlatformAuditResponse>>;
  }
}
